import { AddIcon, Button, LazyLoader, Modal } from '@bp/ui-components';
import { FC, Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import {
  useCreateTimetableVersionMutation,
  useDuplicateTimetableVersionMutation,
  useTimetablesQuery,
} from '../../../types/planung-graphql-client-defs';
import { showSuccessDuplicateToast, showUserErrorToast } from '../../../utils/toast';
import { TIMETABLE_QUERY_CONTEXT } from '../../Timetable/hooks/useTimetables';
import { CopyVersionToNewTimetableModal, CopyVersionToNewTimetableValues } from '../CopyVersionToNewTimetableModal';
import { TimetableVersion, TimetableVersionsOverview } from './TimetableVersionsOverview';
import styles from './TimetableVersionsOverview.module.scss';

type TimetableVersionsOverviewModalProps = {
  timetableUuid: string | null;
  draftName: string;
  isOpen: boolean;
  onClose: () => void;
};

export const TimetableVersionsOverviewModal: FC<TimetableVersionsOverviewModalProps> = ({
  timetableUuid,
  draftName,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();
  const context = useMemorizedCacheTag('TIMETABLE_VERSIONS');

  const [loading, setLoading] = useState<boolean>(false);
  const [, createVersion] = useCreateTimetableVersionMutation();
  const [, duplicateVersion] = useDuplicateTimetableVersionMutation();

  // „Kopieren nach → Neuer Stundenplan“ läuft in einem eigenen Modal. Solange es offen ist, wird die
  // Versionsübersicht ausgeblendet – zwei Modals übereinander würden den Hintergrund doppelt abdunkeln.
  const [versionToCopy, setVersionToCopy] = useState<TimetableVersion | null>(null);

  // Alle Schuljahre: der Name des neuen Stundenplans muss über alle eindeutig sein.
  const [{ data: timetableData }] = useTimetablesQuery({
    variables: { schoolYearUuid: null },
    context: TIMETABLE_QUERY_CONTEXT,
    pause: !isOpen,
  });
  const timetable = timetableData?.timetables.find((tt) => tt.uuid === timetableUuid);

  // Der neue Stundenplan muss zusätzlich in der Stundenplan-Liste auftauchen.
  const newTimetableContext = useMemo(() => {
    return {
      additionalTypenames: ['Timetable', 'TimetableItem', 'TimetableVersion', 'TimetableCard', 'LessonClass'],
    };
  }, []);

  const onCopyToNewTimetable = async (values: CopyVersionToNewTimetableValues): Promise<boolean> => {
    if (!versionToCopy) return false;
    const result = await duplicateVersion(
      {
        timetableVersionUuid: versionToCopy.uuid,
        options: {
          createNewTimetable: true,
          newTimetable: {
            name: values.name,
            comment: values.comment,
            timeGridUuid: values.timeGridUuid,
            mapping: values.migration?.mapping,
            resetAvailabilities: values.migration?.resetAvailabilities,
          },
        },
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      newTimetableContext,
    );
    if (!result || result.error) {
      showUserErrorToast({ error: result.error });
      return false;
    }
    showSuccessDuplicateToast();
    // Die Kopie liegt in einem anderen Stundenplan – die Versionsübersicht dieses Plans wird nicht mehr gebraucht.
    onClose();
    return true;
  };

  async function onAdd() {
    if (timetableUuid) {
      setLoading(true);
      await createVersion(
        {
          timetableUuid: timetableUuid,
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
        },
        context,
      );
      setLoading(false);
    }
  }

  return (
    <>
      <Modal
        className={styles['timetable-versions-overview-modal']}
        title={t('timetableVersion.showForDraft', { name: draftName })}
        isOpen={isOpen && !versionToCopy}
        onRequestClose={onClose}
        loading={loading}
        actions={
          <Button disabled={!permissions?.canWriteTimetable()} onClick={onAdd} hierarchy='tertiary' icon={<AddIcon />}>
            {t('timetableVersion.add')}
          </Button>
        }
      >
        <Suspense fallback={<LazyLoader embedded forceHeight='25vh' />}>
          <TimetableVersionsOverview
            timetableUuid={timetableUuid}
            onCopyToNewTimetable={setVersionToCopy}
            onNavigate={() => setLoading(true)}
            setLoading={setLoading}
          />
        </Suspense>
      </Modal>
      {/* Eigene Suspense-Grenze: die Queries des Modals sollen nichts anderes durch einen Loader ersetzen. */}
      <Suspense fallback={null}>
        <CopyVersionToNewTimetableModal
          existingTimetableNames={timetableData?.timetables.map((tt) => tt.name) ?? []}
          isOpen={isOpen && !!versionToCopy}
          sourceComment={timetable?.comment ?? ''}
          // Default-Zeitraster = Config ohne Klassen-Einschränkung
          sourceTimeGridUuid={
            timetable?.timegridConfigs.find((config) => config.classUuids.length === 0)?.timegridUuid ?? null
          }
          sourceTimetableName={timetable?.name ?? ''}
          version={versionToCopy}
          onClose={() => setVersionToCopy(null)}
          onSubmit={onCopyToNewTimetable}
        />
      </Suspense>
    </>
  );
};
